import { t } from 'i18next';
import { useSelector } from 'react-redux';
import { authStateSelector, specsSelector } from '../../../../../../../shared/frontend/scripts/selectors/global';
import useFeaturedRecruitmentProfiles from '../../../shared/recruitment/hooks/useFeaturedRecruitmentProfiles';
import useRecentRecruitmentProfiles from '../../../shared/recruitment/hooks/useRecentRecruitmentProfiles';
import { Link } from '../../../../../../../shared/frontend/components/link';
import FeaturedProfiles from './featuredProfile';
import RecruitmentRecentProfiles from './recentProfiles';
import classnames from 'classnames';
import apiService from '../../../services/apiService';
import { actions as RecruitmentActions } from '../../../shared/recruitment/modules';
import _ from 'lodash';

export type TRecruitmentPaneProps = {
  region: string;
  enabled: boolean;
};

const RecruitmentPane = (props: TRecruitmentPaneProps) => {
  const { region, enabled } = props;

  if (!enabled) {
    return null;
  }

  const auth = useSelector(authStateSelector);
  const specs = useSelector(specsSelector);
  const featuredProfiles = useFeaturedRecruitmentProfiles(region);
  const recentlyPublishedProfiles = useRecentRecruitmentProfiles(region);

  return (
    <div className="slds-size--1-of-1 rio-recruitment-pane">
      <div
        className={classnames(
          'slds-grid',
          'slds-grid--align-spread',
          'slds-grid--vertical-align-end',
          'rio-m-bottom-mobi--none',
          'slds-m-top--large'
        )}
      >
        <div className="slds-col">
          <h2 className="rio-text-title--caps text-raiderio">{t('frontpage:recruitment.title')}</h2>
        </div>
        <div className="slds-col">
          {auth?.authenticated ? (
            <Link to="/settings/recruitment/profiles/create">
              {t('frontpage:recruitment.create-recruitment-profile')}
            </Link>
          ) : (
            <Link to="/recruitment">{t('frontpage:recruitment.about-recruitment')}</Link>
          )}
        </div>
      </div>
      <div className="recruitment-pane-content slds-grid slds-grid--pull-padded slds-grid--vertical-stretch slds-wrap">
        <div
          className={classnames(
            'recruitment-pane-table',
            'slds-size--6-of-12',
            'slds-max-small-size--1-of-1',
            'slds-max-medium-size--1-of-1',
            'slds-max-large-size--3-of-3',
            'slds-order--1',
            'slds-max-small-order--2',
            'slds-max-medium-order--2',
            'slds-max-large-order--2',
            'slds-p-horizontal--small',
            'slds-m-vertical--small'
          )}
        >
          <RecruitmentRecentProfiles specs={specs} {...recentlyPublishedProfiles} />
        </div>
        <div
          className={classnames(
            'recruitment-pane-carousel',
            'slds-size--6-of-12',
            'slds-max-small-size--1-of-1',
            'slds-max-medium-size--1-of-1',
            'slds-max-large-size--3-of-3',
            'slds-order--2',
            'slds-max-small-order--1',
            'slds-max-medium-order--1',
            'slds-max-large-order--1',
            'slds-m-vertical--small',
            'slds-p-horizontal--small',
            'rio-hidden--x-small'
          )}
        >
          <div className="slds-grid slds-grid--vertical-align-center" style={{ height: '100%' }}>
            <FeaturedProfiles {...featuredProfiles} />
          </div>
        </div>
      </div>
    </div>
  );
};

(RecruitmentPane as any).preloadComponentActions = async (props: TRecruitmentPaneProps, params: any) => {
  if (!props.enabled) {
    return [];
  }

  const { region } = props;

  return _.flatten(
    await Promise.all([
      new Promise((resolve, reject) => {
        const loadQueryParams = { limit: 8, region };
        apiService.getRecentRecruitmentProfiles(loadQueryParams, (err, data) => {
          if (err) {
            reject(err);
          } else {
            resolve([RecruitmentActions.loadRecentlyPublishedProfilesSuccess(data.profiles)]);
          }
        });
      }),
      new Promise((resolve, reject) => {
        const loadQueryParams = { limit: 10, region };
        apiService.getFeaturedRecruitmentProfiles(loadQueryParams, (err, data) => {
          if (err) {
            reject(err);
          } else {
            resolve([RecruitmentActions.loadFeaturedProfilesSuccess(data.profiles)]);
          }
        });
      })
    ])
  );
};

export default RecruitmentPane;
